import {useMemo, useState} from 'react'
import {
  AlertCircle,
  Building2,
  ChevronDown,
  ChevronRight,
  ClipboardPlus,
  Loader2,
  Package,
  Search,
  TrendingUp,
  X,
} from 'lucide-react'
import {ProductWithCalculations, PurchaseOrderItem} from '../shared/types/inventory'
import {Badge} from '../shared/components/Badge'
import {ProductThumbnail} from '../shared/components/ProductThumbnail'
import {getStatusInfo} from '../shared/utils/inventoryCalculations'

function formatCnpj(cnpj: string) {
  const digits = cnpj.replace(/\D/g, '')
  if (digits.length !== 14) return cnpj
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12)}`
}

function buildItems(products: ProductWithCalculations[]): PurchaseOrderItem[] {
  return products
    .filter((product) => product.pedidoSugerido > 0)
    .map((product) => {
      let prioridade: PurchaseOrderItem['prioridade'] = 'baixa'
      if (product.status === 'ruptura' || product.status === 'iminente') prioridade = 'alta'
      else if (product.status === 'minimo' || product.status === 'critica') prioridade = 'media'
      const quantidade = Math.ceil(product.pedidoSugerido)
      return {
        product,
        quantidade,
        fornecedor: product.fornecedor || 'Fornecedor não informado',
        prioridade,
      }
    })
    .sort((first, second) => ({alta: 0, media: 1, baixa: 2}[first.prioridade] - {alta: 0, media: 1, baixa: 2}[second.prioridade]))
}

function PriorityBadge({priority}: {priority: PurchaseOrderItem['prioridade']}) {
  const config = {
    alta: {label: 'Alta prioridade', color: 'text-red-900', bgColor: 'bg-red-100'},
    media: {label: 'Média prioridade', color: 'text-yellow-900', bgColor: 'bg-yellow-100'},
    baixa: {label: 'Baixa prioridade', color: 'text-gray-900', bgColor: 'bg-gray-100'},
  }[priority]
  return <Badge label={config.label} color={config.color} bgColor={config.bgColor} />
}

interface PurchaseOrderCNPJSectionProps {
  cnpj: string
  razaoSocial: string
  products: ProductWithCalculations[]
  isExpanded: boolean
  onToggle: () => void
  onCreatePreOrder: () => void
  creatingPreOrder: boolean
  createDisabled: boolean
}

export function PurchaseOrderCNPJSection({
  cnpj,
  razaoSocial,
  products,
  isExpanded,
  onToggle,
  onCreatePreOrder,
  creatingPreOrder,
  createDisabled,
}: PurchaseOrderCNPJSectionProps) {
  const [search, setSearch] = useState('')
  const items = useMemo(() => buildItems(products), [products])
  const visibleItems = useMemo(() => {
    const query = search.toLowerCase().trim()
    if (!query) return items
    return items.filter((item) =>
      [item.product.nome, item.product.ean, item.fornecedor].join(' ').toLowerCase().includes(query)
    )
  }, [items, search])
  const totals = useMemo(() => ({
    items: items.length,
    units: items.reduce((sum, item) => sum + item.quantidade, 0),
    high: items.filter((item) => item.prioridade === 'alta').length,
    medium: items.filter((item) => item.prioridade === 'media').length,
  }), [items])

  return (
    <div className='overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-all'>
      <button type='button' onClick={onToggle} className='flex w-full items-center justify-between px-6 py-4 text-left transition hover:bg-gray-50'>
        <div className='flex items-center gap-4'>
          <span className='text-gray-500'>{isExpanded ? <ChevronDown className='h-5 w-5' /> : <ChevronRight className='h-5 w-5' />}</span>
          <span className='rounded-lg border border-teal-100 bg-teal-50 p-2 text-teal-600'><Building2 className='h-5 w-5' /></span>
          <div>
            <div className='flex items-center gap-3'><h3 className='font-mono text-base font-bold tracking-wide text-gray-900'>{formatCnpj(cnpj)}</h3>{razaoSocial && <span className='text-sm font-normal text-gray-500'>{razaoSocial}</span>}</div>
            <p className='mt-0.5 text-xs text-gray-500'>{totals.items} {totals.items === 1 ? 'item' : 'itens'} para reposição</p>
          </div>
        </div>
        <div className='flex items-center gap-3'>
          {totals.high > 0 && <span className='inline-flex items-center gap-1.5 rounded-full border border-red-100 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700'><span className='h-1.5 w-1.5 animate-pulse rounded-full bg-red-500' />{totals.high} urgente{totals.high > 1 ? 's' : ''}</span>}
          <span className='rounded-full border border-teal-100 bg-teal-50 px-2.5 py-1 text-xs font-semibold text-teal-700'>{totals.units} un</span>
        </div>
      </button>

      {isExpanded && (
        <div className='border-t border-gray-100'>
          <div className='px-6 pb-2 pt-5'>
            <div className='mb-5 grid grid-cols-1 gap-4 md:grid-cols-3'>
              <div className='rounded-lg border border-gray-100 bg-gray-50 p-4'><div className='flex items-center justify-between'><div><p className='text-xs font-medium uppercase tracking-wider text-gray-500'>Itens diferentes</p><p className='mt-1 text-2xl font-bold text-gray-900'>{totals.items}</p></div><span className='rounded-full bg-blue-100 p-2.5 text-blue-700'><Package className='h-5 w-5' /></span></div></div>
              <div className='rounded-lg border border-gray-100 bg-gray-50 p-4'><div className='flex items-center justify-between'><div><p className='text-xs font-medium uppercase tracking-wider text-gray-500'>Total de unidades</p><p className='mt-1 text-2xl font-bold text-gray-900'>{totals.units}</p></div><span className='rounded-full bg-teal-100 p-2.5 text-teal-700'><TrendingUp className='h-5 w-5' /></span></div></div>
              <div className='rounded-lg border border-gray-100 bg-gray-50 p-4'><div className='flex items-center justify-between'><div><p className='text-xs font-medium uppercase tracking-wider text-gray-500'>Urgentes</p><p className='mt-1 text-2xl font-bold text-gray-900'>{totals.high}</p><p className='mt-0.5 text-xs text-gray-500'>{totals.medium} médias</p></div><span className='rounded-full bg-red-100 p-2.5 text-red-700'><AlertCircle className='h-5 w-5' /></span></div></div>
            </div>

            <div className='mb-4 flex flex-col gap-3 md:flex-row md:items-center'>
              <div className='relative w-full max-w-md flex-1'>
                <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
                <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder='Buscar por produto, EAN ou fornecedor...' className='w-full rounded-lg border border-gray-200 bg-white py-2.5 pl-10 pr-10 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100' />
                {search && <button type='button' onClick={() => setSearch('')} className='absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'><X className='h-4 w-4' /></button>}
              </div>
              <span className='text-sm text-gray-500'>{visibleItems.length} de {items.length} itens</span>
              <button
                type='button'
                onClick={onCreatePreOrder}
                disabled={items.length === 0 || createDisabled}
                className='ml-auto inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-40'
              >
                {creatingPreOrder ? <Loader2 className='h-4 w-4 animate-spin' /> : <ClipboardPlus className='h-4 w-4' />}
                {creatingPreOrder ? 'Criando pré-pedido...' : 'Criar pré-pedido'}
              </button>
            </div>
          </div>

          <div className='overflow-x-auto'>
            <table className='w-full min-w-[840px]'>
              <thead className='border-y border-gray-200 bg-gray-50 text-left text-xs font-semibold uppercase tracking-wider text-gray-700'>
                <tr><th className='px-6 py-3'>Produto</th><th className='px-6 py-3'>Fornecedor</th><th className='px-6 py-3'>Status atual</th><th className='px-6 py-3 text-right'>Quantidade sugerida</th><th className='px-6 py-3 text-center'>Prioridade</th></tr>
              </thead>
              <tbody className='divide-y divide-gray-200'>
                {visibleItems.length === 0 ? (
                  <tr><td colSpan={5} className='px-6 py-12 text-center'><Package className='mx-auto mb-2 h-8 w-8 text-gray-300' /><p className='font-medium text-gray-500'>Nenhum item encontrado</p></td></tr>
                ) : visibleItems.map((item) => {
                  const status = getStatusInfo(item.product.status)
                  return (
                    <tr key={`${cnpj}-${item.product.id}`} className='transition hover:bg-gray-50'>
                      <td className='px-6 py-4'><div className='flex items-center gap-3'><ProductThumbnail src={item.product.foto} alt={item.product.nome} ean={item.product.ean} details={{
                        status: item.product.status, statusLabel: item.product.statusLabel, estoqueAtual: item.product.estoqueAtual,
                        cmd: item.product.cmd, cobertura: item.product.cobertura, leadTime: item.product.leadTime,
                        estoqueAlvo: item.product.estoqueAlvo, pedidoSugerido: item.product.pedidoSugerido, fornecedor: item.fornecedor,
                      }} /><div className='min-w-0'><p className='max-w-[320px] truncate text-sm font-medium text-gray-900'>{item.product.nome}</p><p className='text-xs text-gray-500'>EAN: {item.product.ean}</p></div></div></td>
                      <td className='px-6 py-4 text-sm text-gray-600'>{item.fornecedor}</td>
                      <td className='px-6 py-4'><Badge label={status.label} color={status.color} bgColor={status.bgColor} /></td>
                      <td className='px-6 py-4 text-right text-sm font-semibold text-gray-900'>{item.quantidade} un</td>
                      <td className='px-6 py-4 text-center'><PriorityBadge priority={item.prioridade} /></td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
